import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Star, ChevronLeft, ChevronRight } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

/**
 * Google reviews slider for the home page. Ported from the Meta landing pages
 * (packages/meta-lp/src/components/ReviewCarousel.tsx): same badge, card,
 * avatars, dots and rotation behaviour. Everything is served locally: the
 * Google "G" is /google-g.png (official file, unchanged), avatars are initials.
 *
 * Google's brand rules: say "on Google", no stars next to the Google name or
 * logo. By decision: no "as of" date, no review count, reviews shortened only
 * with "…" (in constants.ts).
 */
export const GOOGLE_REVIEWS_URL = 'https://maps.google.com/?cid=11654920282304290221';

const INTERVAL_MS = 8000; // landing pages: 4.5 s, but these reviews are longer
const SWIPE_PX = 40;
const GAP_PX = 24; // gap between cards on tablet/desktop

/** Muted avatar colours, picked per reviewer name (stable across renders). */
const AVATAR_COLORS = ['#5f6368', '#1a73e8', '#188038', '#c5221f', '#e37400', '#9334e6', '#007b83'];
const avatarColor = (name: string) =>
  AVATAR_COLORS[[...name].reduce((sum, ch) => sum + ch.charCodeAt(0), 0) % AVATAR_COLORS.length];

type GoogleReview = { name: string; text: string };

const useIsomorphicLayoutEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect;

/** Name, 5 stars, text: identical on mobile and desktop. */
const ReviewFigure: React.FC<{ review: GoogleReview; starsLabel: string; className?: string; hidden?: boolean }> = ({
  review: r,
  starsLabel,
  className = '',
  hidden,
}) => (
  <figure className={className} aria-hidden={hidden}>
    <figcaption className="flex items-center gap-2.5">
      <span
        aria-hidden="true"
        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-[14px] font-semibold text-white"
        style={{ backgroundColor: avatarColor(r.name) }}
      >
        {r.name.charAt(0)}
      </span>
      <span className="min-w-0">
        <span className="block text-[14px] font-semibold leading-tight text-pstax-navy">{r.name}</span>
        <span className="flex text-[#FBBC04]" role="img" aria-label={starsLabel}>
          {[0, 1, 2, 3, 4].map((n) => (
            <Star key={n} size={12} fill="currentColor" strokeWidth={0} />
          ))}
        </span>
      </span>
    </figcaption>
    <blockquote className="mt-2 text-[15px] leading-[1.5] text-pstax-navy/90">“{r.text}”</blockquote>
  </figure>
);

/**
 * Mobile (< 768 px): one card, reviews fade in place (as on the landing pages).
 * Tablet: 2 cards side by side, desktop (≥ 1024 px): 3 cards. That track moves
 * one card per step and loops endlessly: the first 3 reviews are appended as
 * clones, and after sliding onto them it jumps back to the start without
 * animation. Both layouts are in the DOM, CSS shows one of them.
 */
export const GoogleReviews: React.FC = () => {
  const { t, language } = useLanguage();
  const g = t.googleReviews;
  const reviews: GoogleReview[] = g.items;
  const n = reviews.length;
  const isDe = language === 'DE';

  const [index, setIndex] = useState(0); // mobile: active review
  const [pos, setPos] = useState(0); // tablet/desktop: first visible card, 0..n (n = clone of 0)
  const [jump, setJump] = useState(false); // move the track without animation
  const [jumpSeq, setJumpSeq] = useState(0); // re-arms the jump effect for back-to-back jumps
  const [paused, setPaused] = useState(false);
  const [reduced, setReduced] = useState(true); // SSR + first paint: static
  const [tick, setTick] = useState(0); // restarts the timer after a manual move
  const startX = useRef<number | null>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const posRef = useRef(0); // current pos, readable without a state updater
  const pendingPos = useRef<number | null>(null); // slide target applied right after a jump

  const movePos = useCallback((p: number) => {
    posRef.current = p;
    setPos(p);
  }, []);
  /** Jump to `to` without animation, then (optionally) slide to `then`. */
  const jumpTo = useCallback(
    (to: number, then: number | null = null) => {
      pendingPos.current = then;
      setJump(true);
      setJumpSeq((x) => x + 1);
      movePos(to);
    },
    [movePos]
  );

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReduced(mq.matches);
    const onChange = () => setReduced(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  // New language → start again at the first review.
  useEffect(() => {
    setIndex(0);
    movePos(0);
  }, [reviews, movePos]);

  // After a jump: force a reflow so the browser applies the new position
  // without animation, then re-enable the animation (and slide on, if asked).
  useIsomorphicLayoutEffect(() => {
    if (!jump) return;
    void trackRef.current?.offsetWidth;
    setJump(false);
    if (pendingPos.current !== null) movePos(pendingPos.current);
    pendingPos.current = null;
  }, [jump, jumpSeq, movePos]);

  const step = useCallback(
    (delta: number) => {
      setIndex((i) => (i + delta + n) % n);
      const p = posRef.current;
      if (reduced) movePos((p + delta + n) % n);
      // At the start: jump to the clone of review 0, then slide back one card.
      else if (delta < 0 && p === 0) jumpTo(n, n - 1);
      // On the clones: jump back to the real review 0, then slide on.
      else if (delta > 0 && p >= n) jumpTo(0, 1);
      else movePos(p + delta);
    },
    [n, reduced, movePos, jumpTo]
  );

  const go = useCallback(
    (delta: number) => {
      step(delta);
      setTick((x) => x + 1);
    },
    [step]
  );

  useEffect(() => {
    if (reduced || paused) return;
    const id = window.setInterval(() => {
      if (document.visibilityState === 'visible') step(1);
    }, INTERVAL_MS);
    return () => window.clearInterval(id);
  }, [reduced, paused, tick, step]);

  // Slid onto the clones → jump back to the real first review.
  const onTrackTransitionEnd = (e: React.TransitionEvent) => {
    if (e.target !== e.currentTarget || posRef.current < n) return;
    jumpTo(posRef.current - n);
  };

  const onPointerDown = (e: React.PointerEvent) => {
    startX.current = e.clientX;
    setPaused(true);
  };
  const onPointerUp = (e: React.PointerEvent) => {
    if (startX.current !== null) {
      const dx = e.clientX - startX.current;
      if (Math.abs(dx) > SWIPE_PX) go(dx < 0 ? 1 : -1);
    }
    startX.current = null;
    if (e.pointerType !== 'mouse') setPaused(false);
  };
  const onPointerCancel = () => {
    startX.current = null;
    setPaused(false);
  };
  const swipe = { onPointerDown, onPointerUp, onPointerCancel };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowRight') go(1);
    if (e.key === 'ArrowLeft') go(-1);
  };

  const dots = (active: number) => (
    <span className="flex gap-1" aria-hidden="true">
      {reviews.map((r, i) => (
        <span
          key={r.name}
          className={`h-1.5 rounded-full transition-all ${i === active ? 'w-4 bg-pstax-navy' : 'w-1.5 bg-pstax-border'}`}
        />
      ))}
    </span>
  );

  const showAll = (
    <a
      href={GOOGLE_REVIEWS_URL}
      target="_blank"
      rel="noopener noreferrer"
      className="whitespace-nowrap text-[13px] font-semibold text-pstax-link underline underline-offset-2"
    >
      {g.showAll} →
    </a>
  );

  const arrowClass =
    'flex items-center justify-center w-10 h-10 rounded-full border border-pstax-border bg-white text-pstax-navy hover:bg-pstax-bg transition-colors focus:outline-none focus:ring-2 focus:ring-pstax-blue shadow-sm cursor-pointer';

  return (
    <section id="reviews" className="pt-8 pb-16 bg-pstax-bg overflow-hidden scroll-mt-20">
      <div className="container mx-auto px-6">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-display font-bold mb-4">{t.reviews.title}</h2>
          <p className="text-pstax-gray max-w-2xl mx-auto">{t.reviews.sub}</p>
        </div>

        <div
          aria-roledescription="carousel"
          aria-label={g.carouselLabel}
          className="max-w-2xl md:max-w-6xl mx-auto select-none"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
          onFocus={() => setPaused(true)}
          onBlur={() => setPaused(false)}
        >
          <a
            href={GOOGLE_REVIEWS_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="flex w-fit items-center text-[13px] leading-tight text-pstax-navy/80 hover:text-pstax-navy"
          >
            <img src="/google-g.png" width={36} height={36} alt="Google" className="-ml-2 shrink-0" />
            <span>
              <span className="font-bold text-pstax-navy">{g.rating}</span>
            </span>
          </a>
          <p className="mb-2 text-[12px] text-pstax-gray">{g.disclaimer}</p>

          {/* Mobile: one card */}
          <div
            className="grid cursor-grab rounded-2xl border border-pstax-border bg-white px-4 pt-4 pb-3 shadow-[0_1px_3px_rgba(15,23,42,0.08)] md:hidden"
            style={{ touchAction: 'pan-y' }}
            tabIndex={0}
            aria-live={paused || reduced ? 'polite' : 'off'}
            onKeyDown={onKeyDown}
            {...swipe}
          >
            {reviews.map((r, i) => (
              <ReviewFigure
                key={r.name}
                review={r}
                starsLabel={g.starsLabel}
                hidden={i !== index}
                className={`[grid-area:1/1] transition-[opacity,visibility] duration-300 motion-reduce:transition-none ${
                  i === index ? 'visible opacity-100' : 'invisible opacity-0'
                }`}
              />
            ))}

            <div className="mt-3 flex items-center justify-between gap-3 [grid-area:2/1]">
              {dots(index)}
              {showAll}
            </div>
          </div>

          {/* Tablet: 2 cards, desktop: 3 cards */}
          <div className="hidden md:block [--per:2] lg:[--per:3]">
            <div
              className="overflow-hidden cursor-grab -m-1 p-1"
              style={{ touchAction: 'pan-y' }}
              tabIndex={0}
              onKeyDown={onKeyDown}
              {...swipe}
            >
              <div
                ref={trackRef}
                className={`flex ${jump || reduced ? '' : 'transition-transform duration-500 ease-in-out'}`}
                style={{
                  gap: GAP_PX,
                  transform: `translateX(calc(${-pos} * (100% + ${GAP_PX}px) / var(--per)))`,
                }}
                onTransitionEnd={onTrackTransitionEnd}
              >
                {[...reviews, ...reviews.slice(0, 3)].map((r, i) => (
                  <div
                    key={i}
                    className="shrink-0 rounded-2xl border border-pstax-border bg-white px-6 pt-5 pb-5 shadow-[0_1px_3px_rgba(15,23,42,0.08)]"
                    style={{ width: `calc((100% - (var(--per) - 1) * ${GAP_PX}px) / var(--per))` }}
                    aria-hidden={i >= n || undefined}
                  >
                    <ReviewFigure review={r} starsLabel={g.starsLabel} />
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-6 flex items-center justify-between gap-6">
              {dots(pos % n)}
              <div className="flex items-center gap-6">
                {showAll}
                <div className="flex gap-3">
                  <button type="button" onClick={() => go(-1)} className={arrowClass} aria-label={isDe ? 'Vorherige Bewertung' : 'Previous review'}>
                    <ChevronLeft size={20} />
                  </button>
                  <button type="button" onClick={() => go(1)} className={arrowClass} aria-label={isDe ? 'Nächste Bewertung' : 'Next review'}>
                    <ChevronRight size={20} />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
